import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { adminCompanies } from '../../api/adminClient'
import { Search } from 'lucide-react'

export default function AdminCompanies() {
  const { t } = useTranslation('portal')
  const [search, setSearch] = useState('')
  const [verifiedFilter, setVerifiedFilter] = useState<boolean | ''>('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'companies', search, verifiedFilter],
    queryFn: () =>
      adminCompanies
        .list({
          search: search || undefined,
          is_verified: verifiedFilter === '' ? undefined : verifiedFilter,
          limit: 100,
        })
        .then((r) => r.data),
  })

  const items = (data?.items ?? []) as { id: string; name: string; tin: string; is_active: string; is_verified: string; created_at: string }[]
  const total = data?.total ?? 0

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('admin.companies.title')}</h1>
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('admin.companies.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg bg-white border border-gray-300 pl-9 pr-3 py-2 text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 focus:outline-none"
          />
        </div>
        <select
          value={verifiedFilter === '' ? 'all' : verifiedFilter ? 'verified' : 'pending'}
          onChange={(e) => {
            const v = e.target.value
            setVerifiedFilter(v === 'all' ? '' : v === 'verified')
          }}
          className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900"
        >
          <option value="all">{t('admin.companies.allStatus')}</option>
          <option value="verified">{t('admin.companies.verified')}</option>
          <option value="pending">{t('admin.companies.pending')}</option>
        </select>
      </div>
      <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-8 text-gray-500">{t('admin.common.loading')}</div>
        ) : error ? (
          <div className="p-8 text-red-600">{t('admin.companies.failedToLoad')}</div>
        ) : (
          <>
            <div className="px-4 py-2 text-gray-500 text-sm border-b border-gray-200 bg-gray-50">
              {t('admin.companies.companiesCount', { count: total })}
            </div>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500 text-sm bg-gray-50">
                  <th className="p-3">{t('admin.companies.company')}</th>
                  <th className="p-3">{t('admin.companies.tin')}</th>
                  <th className="p-3">{t('admin.common.status')}</th>
                  <th className="p-3">{t('admin.common.created')}</th>
                  <th className="p-3">{t('admin.common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <td className="p-3">
                      <Link
                        to={`/admin/companies/${c.id}`}
                        className="text-emerald-600 hover:underline font-medium"
                      >
                        {c.name}
                      </Link>
                    </td>
                    <td className="p-3 text-gray-600">{c.tin}</td>
                    <td className="p-3">
                      <span
                        className={
                          c.is_verified === 'true'
                            ? 'text-emerald-600'
                            : 'text-amber-600'
                        }
                      >
                        {c.is_verified === 'true' ? t('admin.companies.verified') : t('admin.companies.pending')}
                      </span>
                      {c.is_active === 'false' && (
                        <span className="ml-2 text-red-600">{t('admin.dashboard.suspended')}</span>
                      )}
                    </td>
                    <td className="p-3 text-gray-500 text-sm">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                      <Link
                        to={`/admin/companies/${c.id}`}
                        className="text-gray-600 hover:text-gray-900 text-sm"
                      >
                        {t('admin.common.view')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {items.length === 0 && (
              <div className="p-8 text-gray-500 text-center">{t('admin.companies.noCompaniesFound')}</div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
